import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ItemDetailsService } from '../../../services/item-details.service';

export type ItemStockLotRow = {
    itemNo?: string;
    itemDescription?: string;
    warehouse?: string;
    location?: string;
    lot?: string;
    status?: string;
    expiryDate?: string;
    onHandQty?: number;
    availableQty?: number;
    latestExpiry?: boolean;
};

export type ItemStockLotsResponse = {
    itemNo?: string;
    warehouse?: string;
    itemDescription?: string;
    warehouseAvailableQty?: number;
    latestExpiryDate?: string;
    latestExpiryAvailableQty?: number;
    totalAvailableQty?: number;
    lots?: ItemStockLotRow[];
};

@Component({
    selector: 'app-item-stock-lots-popup',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, AutoCompleteModule, TableModule],
    templateUrl: './item-stock-lots-popup.component.html',
    styleUrls: ['./item-stock-lots-popup.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ItemStockLotsPopupComponent implements OnChanges {
    @Input() visible = false;
    @Input() itemNo = '';
    @Input() warehouse = '';
    @Input() company = '';
    @Input() division = '';

    @Output() visibleChange = new EventEmitter<boolean>();

    selectedSuggestion: any = null;
    suggestions: any[] = [];
    searchingItems = false;
    loadingLots = false;
    errorMessage = '';
    result: ItemStockLotsResponse | null = null;
    selectedItemNo = '';
    private suggestionRequestId = 0;
    private lotsRequestId = 0;

    constructor(private readonly itemDetailsService: ItemDetailsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            this.resetState();
            const prefill = String(this.itemNo || '').trim();
            if (prefill) {
                this.selectedSuggestion = { MBITNO: prefill, FINALRESPNSE: prefill };
                this.loadLots(prefill);
            }
        }
    }

    get lots(): ItemStockLotRow[] {
        return (this.result?.lots || []).filter((row) => Number(row.availableQty) > 0);
    }

    get searchHint(): string {
        if (this.searchingItems) {
            return 'Searching items…';
        }
        if (this.loadingLots) {
            return 'Loading lots…';
        }
        if (!this.selectedItemNo) {
            return 'Type at least 3 characters, then select an item';
        }
        return '';
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.resetState();
        }
        this.visibleChange.emit(visible);
    }

    onSearchModelChange(value: any): void {
        this.selectedSuggestion = value;
        const typed = this.currentQuery(value);
        if (!this.selectedItemNo) {
            return;
        }
        if (!typed || !this.queryMatchesSelectedItem(typed)) {
            this.clearLotsForNewSearch();
        }
    }

    searchItems(event: { query?: string }): void {
        const query = String(event?.query ?? '').trim();
        if (this.selectedItemNo && !this.queryMatchesSelectedItem(query)) {
            this.clearLotsForNewSearch();
        }
        if (query.length < 3) {
            this.suggestions = [];
            this.searchingItems = false;
            return;
        }
        const requestId = ++this.suggestionRequestId;
        this.searchingItems = true;
        this.itemDetailsService.searchItemsV2(query + '*', this.company, this.division, this.warehouse).subscribe({
            next: (resp) => {
                if (requestId !== this.suggestionRequestId) {
                    return;
                }
                this.suggestions = this.formatSuggestions(resp);
                this.searchingItems = false;
            },
            error: () => {
                if (requestId !== this.suggestionRequestId) {
                    return;
                }
                this.suggestions = [];
                this.searchingItems = false;
                this.errorMessage = 'Unable to search items. Please try again.';
            }
        });
    }

    onItemSelected(event: any): void {
        const item = event?.value ?? event;
        const itemNo = String(item?.MBITNO ?? item?.itemNumber ?? '').trim();
        if (!itemNo) {
            this.errorMessage = 'Selected item has no item number.';
            return;
        }
        this.selectedSuggestion = item;
        this.loadLots(itemNo);
    }

    onSuggestionCleared(): void {
        this.suggestions = [];
        this.clearLotsForNewSearch();
    }

    private clearLotsForNewSearch(): void {
        this.lotsRequestId++;
        this.selectedItemNo = '';
        this.result = null;
        this.loadingLots = false;
        this.errorMessage = '';
    }

    private currentQuery(value: any): string {
        if (value == null) {
            return '';
        }
        if (typeof value === 'string') {
            return value.trim();
        }
        return String(value?.FINALRESPNSE ?? value?.MBITNO ?? '').trim();
    }

    private queryMatchesSelectedItem(query: string): boolean {
        const selected = this.selectedItemNo.trim();
        if (!selected || !query) {
            return false;
        }
        return query === selected || query.startsWith(selected + ' | ') || query.startsWith(selected + ' ');
    }

    private loadLots(itemNo: string): void {
        const warehouse = String(this.warehouse || '').trim();
        if (!warehouse) {
            this.errorMessage = 'Warehouse is required.';
            this.result = null;
            return;
        }
        const requestId = ++this.lotsRequestId;
        this.selectedItemNo = itemNo;
        this.loadingLots = true;
        this.errorMessage = '';
        this.itemDetailsService.getItemStockLots(this.company, this.division, warehouse, itemNo).subscribe({
            next: (response) => {
                if (requestId !== this.lotsRequestId) {
                    return;
                }
                this.result = response || { lots: [] };
                this.loadingLots = false;
            },
            error: (err) => {
                if (requestId !== this.lotsRequestId) {
                    return;
                }
                this.result = null;
                this.loadingLots = false;
                this.errorMessage = this.toUserMessage(err, 'Unable to load lot and stock details. Please try again.');
            }
        });
    }

    private resetState(): void {
        this.suggestionRequestId++;
        this.lotsRequestId++;
        this.selectedSuggestion = null;
        this.suggestions = [];
        this.selectedItemNo = '';
        this.result = null;
        this.errorMessage = '';
        this.searchingItems = false;
        this.loadingLots = false;
    }

    private toUserMessage(err: any, fallback: string): string {
        const raw = err?.error;
        const msg = typeof raw === 'string' ? raw.trim() : String(raw?.message ?? '').trim();
        if (!msg) {
            return fallback;
        }
        if (/exception|unresolved compilation|java\.|nested|handler dispatch|stack|at com\./i.test(msg)) {
            return fallback;
        }
        return msg;
    }

    private formatSuggestions(resp: any[]): any[] {
        if (!Array.isArray(resp) || resp.length === 0) {
            return [];
        }
        return resp.map((item) => {
            const itno = String(item?.MBITNO ?? '').trim();
            const label = String(item?.FINALRESPNSE ?? '').trim();
            if (!itno || !label) {
                return item;
            }
            const separator = ' | ';
            if (label.startsWith(itno + separator) || label.startsWith(itno + ' ')) {
                return item;
            }
            return { ...item, FINALRESPNSE: `${itno}${separator}${label}` };
        });
    }
}
